import { supabase } from "@/integrations/supabase/client";
import { track } from "@/lib/posthog";
import type { SigningOutputFormat } from "./outputFormat";

/** One entry of the ordered signer chain sent to `dokobit-sign-start`. */
export interface SigningChainSigner {
  /** `user_profiles.id`. Org members only — there is no external-signer surface. */
  user_id: number;
  /** Optional pre-fill; when supplied Dokobit binds the certificate to this person. */
  personal_code?: string;
  /** `EE` | `LV` | `LT`; the backend defaults to `EE`. */
  country_code?: string;
  /**
   * 1-based. Omit for all entries (the backend then reads the array order) or
   * supply for all — the backend rejects a partially positioned list. Equal
   * positions are a PARALLEL GROUP who may sign in any order.
   */
  position?: number;
}

export interface StartSigningParams {
  buildingId: number;
  documentIds: number[];
  containerName: string;
  outputFormat?: SigningOutputFormat;
  personalCode?: string;
  language?: string;
  /** Project the signing was started from — where the container gets filed. */
  projectId?: number | null;
  /** Folder the signing was started from; wins over projectId. */
  folderId?: number | null;
  /** Ordered chain. Omitted => the caller signs alone (the legacy shape). */
  signers?: SigningChainSigner[];
}

export interface StartSigningResult {
  session_id: string;
  signing_token: string;
  /** How many signers the chain has — 1 for the legacy self-sign shape. */
  signer_count: number;
  /** False when the chain starts with somebody else: there is no iframe to show. */
  your_turn: boolean;
  /** NULL unless it is the caller's turn — do not mount an iframe without it. */
  signer_token: string | null;
  iframe_url: string | null;
  /** Present only when an identical in-flight chain was picked back up. */
  resumed?: boolean;
}

/**
 * `signing_sessions.status`, verbatim. `creating` is included because
 * `dokobit-sign-status` genuinely projects it: its terminal-state short circuit
 * covers only completed/cancelled/failed/expired, so a chain whose header exists
 * but whose Dokobit signing token does not yet is returned as-is. It means
 * "not started yet", never "finished" — treat it as still running.
 */
export type SigningState =
  | "creating"
  | "awaiting_user"
  | "completed"
  | "cancelled"
  | "failed"
  | "expired";

/** The states a chain can still move out of on its own. */
export const RUNNING_SIGNING_STATES: ReadonlySet<SigningState> = new Set<SigningState>([
  "creating",
  "awaiting_user",
]);

/** `signing_session_signers.status`. */
export type SigningSignerState = "pending" | "signed" | "rejected" | "removed";

/** One signer as `dokobit-sign-status` projects it. */
export interface SigningStatusSigner {
  position: number;
  name: string;
  status: SigningSignerState;
  signed_at: string | null;
  method: string | null;
  is_you: boolean;
  /**
   * Whose turn it is right now. Trust this over re-deriving
   * `min(position where pending)` — rows sharing a position are a parallel
   * group, so more than one signer can be current.
   */
  is_current: boolean;
}

export interface SigningStatusResult {
  state: SigningState;
  container_document_id?: number | null;
  error_message?: string | null;
  /** `min(position)` over rows still pending; null once none are. */
  current_position?: number | null;
  signers?: SigningStatusSigner[];
}

/**
 * The viewer has signed, but the chain has NOT finished — somebody after them
 * still has to sign.
 *
 * This is the ordinary outcome when the sender signs first in a chain: their
 * signature lands, `reconcileChain` moves the turn to position 2, and the
 * session stays `awaiting_user`. Watching only whole-session terminal states
 * therefore never resolves, and the sender is left on a spinner forever. The
 * per-signer rows `dokobit-sign-status` projects are what actually ended.
 *
 * `is_you` is the backend's own comparison against the caller's profile id, so
 * no client-side identity guess is involved. Rows sharing a position are a
 * parallel group and one person can only hold one row, but `every` keeps the
 * predicate honest if that ever stops being true.
 */
export function isYourPartDone(result: SigningStatusResult): boolean {
  if (!RUNNING_SIGNING_STATES.has(result.state)) return false;
  const yours = (result.signers ?? []).filter((signer) => signer.is_you);
  return yours.length > 0 && yours.every((signer) => signer.status === "signed");
}

/** How many signers the chain is still waiting on — the "what happens next" count. */
export function remainingSignerCount(result: SigningStatusResult): number {
  return (result.signers ?? []).filter((signer) => signer.status === "pending").length;
}

/**
 * Somebody REFUSED, so {@link remainingSignerCount} reaching 0 does NOT mean
 * every signature is in — a rejected signer is out of the chain exactly as a
 * signed one is, and `computeChainAdvance` fails the whole chain on any
 * rejection, so no container will ever be filed. Anything that phrases "nobody
 * is pending" to a user has to ask this too.
 */
export function hasDeclinedSigner(result: SigningStatusResult): boolean {
  return (result.signers ?? []).some((signer) => signer.status === "rejected");
}

/** What `dokobit-sign-open` hands back — the signer's own turn, and nothing else. */
export interface OpenSignerTurnResult {
  iframe_url: string;
  container_name: string;
  files: Array<{ filename: string; size: number }>;
  position: number;
  signer_count: number;
  signed_count: number;
  expires_at: string;
  signer_name: string;
}

export interface CancelSigningResult {
  state: SigningState;
}

/**
 * Why the backend refused. `dokobit-sign-open` returns the first five on a 403;
 * `dokobit-sign-start` returns those plus `chain_differs` on a 409 when an
 * in-flight chain for the same documents is not the one being requested;
 * `dokobit-sign-cancel` returns the last two when the chain is already past the
 * point where cancelling would discard real signatures.
 */
export type SigningRefusalReason =
  | "unknown_link"
  | "chain_closed"
  | "already_signed"
  | "not_your_turn"
  | "no_longer_a_member"
  | "chain_differs"
  | "finalizing"
  | "fully_signed";

const REFUSAL_REASONS = new Set<string>([
  "unknown_link",
  "chain_closed",
  "already_signed",
  "not_your_turn",
  "no_longer_a_member",
  "chain_differs",
  "finalizing",
  "fully_signed",
]);

/**
 * A refusal the UI can act on rather than only display: "chain_differs" offers
 * "cancel it first", "not_your_turn" is a wait, "fully_signed" is a refresh.
 * The Response body can only be read once, so the reason has to be lifted here
 * or it is lost for good.
 */
export class SigningRefusalError extends Error {
  readonly reason: SigningRefusalReason;
  readonly sessionId: string | null;
  readonly state: string | null;

  constructor(
    message: string,
    reason: SigningRefusalReason,
    sessionId: string | null,
    state: string | null
  ) {
    super(message);
    this.name = "SigningRefusalError";
    this.reason = reason;
    this.sessionId = sessionId;
    this.state = state;
  }
}

/**
 * A refusal that carries no machine-readable `reason` — only an HTTP status.
 *
 * `dokobit-sign-start` answers its validation failures as a bare
 * `{ error: "<English sentence>" }`: "signers: the same person cannot be
 * invited twice", "Not an active member of this organisation: 7", "A signing
 * for these documents is already in progress". That prose is written for a log,
 * it is English-only, and it must never reach an Estonian user's screen. The
 * status is the one part of it that IS a stable machine signal, so it is what
 * `refusalMessage` translates from; `message` stays for the console.
 */
export class SigningRequestError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.name = "SigningRequestError";
    this.status = status;
  }
}

/** Begin a Dokobit Documents Gateway signing session (dokobit-sign-start edge fn). */
export async function startSigning(params: StartSigningParams): Promise<StartSigningResult> {
  const { data, error } = await supabase.functions.invoke("dokobit-sign-start", {
    body: {
      building_id: params.buildingId,
      document_ids: params.documentIds,
      container_name: params.containerName,
      ...(params.outputFormat ? { output_format: params.outputFormat } : {}),
      personal_code: params.personalCode,
      language: params.language ?? "et",
      // Where the container gets filed once signed. The edge function
      // re-verifies both against the building/org before persisting them.
      // Coerced to null rather than left undefined: an absent key and an
      // explicit null resolve the same server-side, but develop's contract
      // (and its test) is an explicit null, so signing outside a
      // project/folder stays byte-identical to the shipped flow.
      project_id: params.projectId ?? null,
      folder_id: params.folderId ?? null,
      // Omitted entirely when absent: sending `signers: undefined` is the same
      // wire shape, and the backend's no-signers path is byte-identical to the
      // behaviour that shipped before chaining.
      ...(params.signers ? { signers: params.signers } : {}),
    },
  });
  if (error) throw await toReadableError(error, "Could not start signing");
  const result = data as StartSigningResult;
  // A resumed session is the SAME chain handed back — re-opening or retrying an in-flight
  // signing creates nothing, so counting it would inflate the metric. The count comes from
  // the backend rather than from `params.signers`, so the property cannot drift from the
  // chain that was actually created; `|| 1` is the legacy self-sign shape, where no chain
  // is sent and an empty one would otherwise report zero signers.
  if (!result.resumed) {
    track("signing_session_started", {
      signer_count: result.signer_count || 1,
      file_count: params.documentIds.length,
      output_format: params.outputFormat ?? "asice",
    });
  }
  return result;
}

/**
 * How long one status read may stay outstanding before it counts as lost.
 *
 * `supabase.functions.invoke` (functions-js 2.4.x) accepts no AbortSignal and
 * passes none to `fetch`, so a request that STALLS rather than fails — a
 * dropped connection, a laptop resumed from sleep, a hung edge function — never
 * rejects on its own. Without a deadline that read is shared forever: every
 * later caller for the session joins the still-in-flight entry below and waits
 * on an answer that is never coming. Nothing else bounds it either — the poll
 * budgets that stop a broken endpoint spinning (MAX_POLL_FAILURES,
 * MAX_SEALING_FAILURES) are spent on REJECTIONS, so a hang charges nothing.
 *
 * 30s is ten times the fast poll cadence and well past a healthy reconcile,
 * which is one container download and upload. The cost of firing early is one
 * duplicate reconcile — the underlying request cannot be cancelled, so its
 * answer is simply dropped — which is why this is a deadline on a lost read,
 * not a tight timeout on a slow one.
 */
const STATUS_READ_TIMEOUT_MS = 30_000;

/** Reject with `message` if `promise` has not settled within `ms`. */
function withDeadline<T>(promise: Promise<T>, ms: number, message: string): Promise<T> {
  let timer = 0;
  // The race's loser is never awaited again: a request that fails LATE, after
  // the deadline already rejected, must not surface as an unhandled rejection.
  // Swallowing is the whole job here — the caller already had its answer.
  promise.catch(() => {
    /* deliberately ignored — see above */
  });
  return Promise.race([
    promise,
    new Promise<never>((_, reject) => {
      timer = window.setTimeout(() => reject(new Error(message)), ms);
    }),
  ]).finally(() => window.clearTimeout(timer));
}

async function readSigningStatus(sessionId: string): Promise<SigningStatusResult> {
  const { data, error } = await withDeadline<Awaited<ReturnType<typeof supabase.functions.invoke>>>(
    supabase.functions.invoke("dokobit-sign-status", {
      body: { session_id: sessionId },
    }),
    STATUS_READ_TIMEOUT_MS,
    // Deliberately the same sentence a failed read shows: to every caller a
    // read that never landed and a read that came back broken are one thing.
    "Could not check signing status"
  );
  if (error) throw await toReadableError(error, "Could not check signing status");
  return data as SigningStatusResult;
}

/**
 * How long a COMPLETED read keeps answering for callers that opted into reuse.
 * Matched to the Signing page's fast cadence (FINALIZING_POLL_MS), so a
 * background watcher sitting over a list that is already polling the same
 * session never issues a second reconcile of its own.
 */
const STATUS_READ_REUSE_MS = 3_000;

interface SharedStatusRead {
  promise: Promise<SigningStatusResult>;
  /**
   * null while the read is still in flight — and every caller that arrives then
   * joins it, which is why the read carries {@link STATUS_READ_TIMEOUT_MS}: an
   * entry that never settles would be a session no caller in the tab can ever
   * read again.
   */
  settledAt: number | null;
}

/**
 * The most recent read per session, shared across every caller in the tab.
 *
 *   ponytail: never pruned. One small entry per session the tab has ever read,
 *   bounded by the Signing page's own SIGNING_LIST_LIMIT and gone on reload.
 */
const statusReads = new Map<string, SharedStatusRead>();

export interface PollSigningStatusOptions {
  /**
   * Accept an answer another caller already got (within
   * {@link STATUS_READ_REUSE_MS}) instead of forcing a fresh reconcile.
   *
   * For a watcher whose only job is to NOTICE a finish — the sign dialog once
   * the signature is in — a three-second-old answer is the same answer, and
   * asking again costs a whole extra container download and upload. A read that
   * has to be current (the tick that notices the chain being cancelled under a
   * live signing surface, or the one Dokobit's `onSignSuccess` triggers) leaves
   * this off.
   */
  reuseRecent?: boolean;
}

/**
 * Poll signing status; the edge fn re-verifies with Dokobit and finalizes when
 * signed.
 *
 * SINGLE-FLIGHT PER SESSION, for every caller in the tab. This is not a
 * micro-optimisation: `dokobit-sign-status` runs `reconcileChain` server-side —
 * a container download and upload — and several independent callers drive the
 * same session at once by design (the Signing page's auto-refresh, the sign
 * dialog open on that row, the detail panel's "Check status" button). Each of
 * them single-flights its own reads, which does nothing about the pair, so the
 * guard belongs here where they meet: a caller arriving while a read is
 * outstanding joins it rather than starting a second reconcile.
 */
export function pollSigningStatus(
  sessionId: string,
  { reuseRecent = false }: PollSigningStatusOptions = {}
): Promise<SigningStatusResult> {
  const shared = statusReads.get(sessionId);
  if (
    shared &&
    (shared.settledAt === null ||
      (reuseRecent && Date.now() - shared.settledAt < STATUS_READ_REUSE_MS))
  ) {
    return shared.promise;
  }

  const entry: SharedStatusRead = { promise: readSigningStatus(sessionId), settledAt: null };
  // A failed read is not an answer and must never be handed to the next caller:
  // it is dropped the moment it rejects, so the following call reads for real.
  entry.promise = entry.promise.then(
    (result) => {
      entry.settledAt = Date.now();
      return result;
    },
    (error: unknown) => {
      if (statusReads.get(sessionId) === entry) statusReads.delete(sessionId);
      throw error;
    }
  );
  statusReads.set(sessionId, entry);
  return entry.promise;
}

/**
 * Open the caller's turn in a chain — the only endpoint that hands out a signer
 * token, and a pure read, so calling it twice returns the same URL.
 *
 * Deliberately NOT gated by `useSigningAccess()`: an invited signer needs no
 * ALLOW_SIGNING to finish a chain they were invited to. A refusal arrives as a
 * {@link SigningRefusalError} carrying the reason.
 */
export async function openSignerTurn(sessionId: string): Promise<OpenSignerTurnResult> {
  const { data, error } = await supabase.functions.invoke("dokobit-sign-open", {
    body: { session_id: sessionId },
  });
  if (error) throw await toReadableError(error, "Could not open this signing");
  return data as OpenSignerTurnResult;
}

/**
 * Call off a chain that is still in flight. The sender (or a platform admin)
 * only — a signer declines inside Dokobit rather than killing the chain.
 * Idempotent: cancelling a cancelled chain returns the same 200. A chain that
 * is already fully signed or mid-finalize refuses with `fully_signed` /
 * `finalizing` rather than discarding signatures that cannot be re-made.
 */
export async function cancelSigning(
  sessionId: string,
  reason?: string
): Promise<CancelSigningResult> {
  const { data, error } = await supabase.functions.invoke("dokobit-sign-cancel", {
    body: { session_id: sessionId, reason },
  });
  if (error) throw await toReadableError(error, "Could not cancel this signing");
  return data as CancelSigningResult;
}

/** The `reason` field of a refusal body, when it is one we know. */
export function refusalReasonOf(body: unknown): SigningRefusalReason | null {
  const reason = (body as { reason?: unknown } | null)?.reason;
  return typeof reason === "string" && REFUSAL_REASONS.has(reason)
    ? (reason as SigningRefusalReason)
    : null;
}

/** A string field of a refusal body, or null — the bodies are hand-written JSON. */
function stringField(body: unknown, key: string): string | null {
  const value = (body as Record<string, unknown> | null)?.[key];
  return typeof value === "string" ? value : null;
}

/**
 * supabase.functions.invoke wraps non-2xx in a FunctionsHttpError whose body
 * holds our `{error, reason?}`.
 *
 * A function that is not deployed yet answers 404 with a body that is not our
 * JSON, so the parse fails and this falls through to the SDK's own message —
 * an ordinary Error the caller already handles, never an unhandled rejection.
 */
async function toReadableError(error: unknown, fallback: string): Promise<Error> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === "function") {
    try {
      const body: unknown = await ctx.json();
      const message = stringField(body, "error");
      if (message) {
        const reason = refusalReasonOf(body);
        return reason
          ? new SigningRefusalError(
              message,
              reason,
              stringField(body, "session_id"),
              stringField(body, "state")
            )
          : new SigningRequestError(message, ctx.status);
      }
    } catch {
      // ignore — fall through to generic message
    }
  }
  return new Error(error instanceof Error ? error.message : fallback);
}
